// import { IAutoSaveDoc } from './../../services/autosave/autosave.interface';
import { SpellCheckStorageService } from './../../services/spell-check/spell-check-storage';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { PaneService } from 'src/app/services/pane.service';
import { PdfInstanceManagerService } from 'src/app/services/instance/pdf-instance-manager.service';
import { HtmlInstanceManagerService } from 'src/app/services/instance/html-instance-manager.service';
import { ModalWindowService } from 'src/app/services/ui/modal-window.service';
import { AsgardTab } from 'src/app/core/asgard-tab';
import { TabService } from 'src/app/services/tab.service';
import { Observable, Subscription } from 'rxjs';
import { AutosaveManagerService } from 'src/app/services/autosave/autosave-manager.service';
// import { AutosaveRestoreDialogComponent } from '../dialogs/autosave-restore-dialog/autosave-restore-dialog.component';
import { AsgardFeatureNames } from 'src/models/AsgardFeatures';
import { SettingsService } from 'src/app/services/settings/settings.service';
import { AutosaveRestoreService } from 'src/app/services/autosave/autosave-restore.service';
import { ExamModeRestrictionsService } from 'src/app/services/exam/exam-mode-restrictions.service';
import { ExtraDocumentsRestrictionService } from 'src/app/services/restrictions/extra-documents-restriction.service';
import { BrowserFocusChecker } from 'src/app/services/exam/browser-focus-checker.service';
import { TranslateModule } from '@ngx-translate/core';
import { TabComponent } from '../tab/tab.component';
import { NoTabBottomToolbarComponent } from '../no-tab-bottom-toolbar/no-tab-bottom-toolbar.component';
import { TooltipDirective } from '../../utils/directives/tooltip.directive';
import { MainClickDirectiveDirective } from '../../utils/directives/main-click-directive.directive';
import { PaneIndexDirective } from '../../utils/directives/pane-index.directive';
import { NgClass, AsyncPipe } from '@angular/common';
import { EpubInstanceManagerService } from 'src/app/services/instance/epub/epub-instance-manager.service';

@Component({
    selector: 'asg-pane-list',
    templateUrl: './pane-list.component.html',
    styleUrls: ['./pane-list.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [NgClass, PaneIndexDirective, MainClickDirectiveDirective, TooltipDirective, NoTabBottomToolbarComponent, TabComponent, AsyncPipe, TranslateModule]
})
export class PaneListComponent implements OnInit, OnDestroy {
  private readonly subscriptions: Subscription = new Subscription();

  panesLimit: boolean = false;
  tabs: Observable<AsgardTab[]>;
  activePaneIndex: number;
  AsgardFeatureNames: typeof AsgardFeatureNames = AsgardFeatureNames;
  examRestrictionsAreActive = false;

  get isDoublePanes(): boolean {
    return this.paneService.isDoublePanes;
  }

  constructor(
    private readonly paneService: PaneService,
    private readonly pdfInstanceSvc: PdfInstanceManagerService,
    private readonly froalaInstanceSvc: HtmlInstanceManagerService,
    protected readonly modal: ModalWindowService,
    private readonly tabService: TabService,
    private readonly spellCheckStorageService: SpellCheckStorageService,
    private readonly autosave: AutosaveManagerService,
    private readonly settings: SettingsService,
    private readonly autosaveRestoreDialogService: AutosaveRestoreService,
    private readonly examService: ExamModeRestrictionsService,
    private readonly extraDocumentsRestrictionService: ExtraDocumentsRestrictionService,
    private readonly browserFocusChecker: BrowserFocusChecker,
    private readonly epubInstanceSvc: EpubInstanceManagerService,
  ) {
  }

  ngOnInit(): void {
    this.getLimit();
    this.getTabs();
    this.autosave.addAutosaveSubscriptions();
    this.autosave.openRestoreDialog.subscribe(e => {
      this.autosaveRestoreDialogService.openRestoreDialog(e);
    });
    // this.autosave.autosaveInitObservable.subscribe((loaded: boolean) => {
    //   if (loaded) {
    //     if (this.autosave.autoSaveEnabled) {
    //       this.autosave.getUnsavedChanges().then((e: Partial<IAutoSaveDoc>[]) => {
    //         if (e && e.length) {
    //           this.modal.createModalWindow(AutosaveRestoreDialogComponent, e);
    //         }
    //       });
    //     }
    //   }
    // })

    this.paneService.checkPanesOnInit();

    this.subscriptions.add(
      this.examService.isExamInProgress$.subscribe(isActive => this.examRestrictionsAreActive = isActive)
    );
  }

  ngOnDestroy(): void {
    this.closeAllTabs(); // remove from memory when logging out
    this.paneService.cleanOndestroy();
    this.tabService.closeTabObservable();
    this.pdfInstanceSvc.cleanOndestroy();
    this.froalaInstanceSvc.cleanOndestroy();
    this.modal.cleanOndestroy();
    this.spellCheckStorageService.cleanOnDestroy();
    this.subscriptions.unsubscribe();
    this.autosave.cleanOnDestroy();
    this.settings.cleanOnDestroy();
    this.epubInstanceSvc.cleanOndestroy();
  }

  selectTab = (tab: AsgardTab) => {
    this.tabService.selectTab(tab.id);
  }

  addNewTab = () => {
    this.tabService.addNewTab();
  }

  isExamFile(index: number): boolean {
    return this.examService.isExamInProgress && index === 0;
  }

  closeTab = async (tab: AsgardTab) => {
    let lastPdfDuringExamCreation = this.examService.notAllowedToCloseDocumentTab && !!tab.file
      // if it was an attachment during test mode you were trying to close
      // don't show the warning
      && !(this.examService.isTestModeActive && this.extraDocumentsRestrictionService.isAttachment(tab.file))
      
    if (lastPdfDuringExamCreation) {
      const result = await this.tabService.prepareCloseTab();

      if (result) {
        this.settings.restoreExamSettings();
        await this.close(tab);
        return;
      }
    }

    const unsavedChanged = await this.autosave.isDocumentUnsavedChanges(tab.id);
    
    this.browserFocusChecker.pauseCheckingDocumentFocus();

    if (unsavedChanged !== null) {
      const result = await this.tabService.prepareCloseTab(unsavedChanged ? true : false);

      if (result) {
        await this.close(tab);
      }

      return;
    }

    await this.close(tab);
  }

  private async close(tab: AsgardTab): Promise<void> {
    await this.autosave.removeUnsavedChangesAndFile(tab.id);
    this.tabService.closeTab(tab.id);
  }

  private closeAllTabs = () => {
    const tabSub = this.tabs.subscribe((tabs) => {
      if (tabs.length > 0) {
        this.tabService.closeAllTabs();
      }
    });
    tabSub.unsubscribe();
  }

  private getLimit(): void {
    this.subscriptions.add(
      this.paneService.getPanesLimit().subscribe(e => this.panesLimit = e)
    );
    this.subscriptions.add(
      this.paneService.getActivePaneObservable().subscribe(e => this.activePaneIndex = e)
    );
  }

  private getTabs(): void {
    this.tabs = this.tabService.getCurrentTabs();
  }
}
